import { FC, ReactNode } from 'react';
import { Link } from 'react-location';
import { Drawer } from 'components';
import _ from 'lodash';
import moment from 'moment';
import numeral from 'numeral';
import { GetTransactions_getTransactions } from '_graphql/queries/__generated__/GetTransactions';
import { useOrderItem } from '../hooks/use-order-item';
import {
  getMetadataDescription,
  parseTransactionMetadata,
} from '../utils/transaction-metadata';

type Props = {
  transaction: GetTransactions_getTransactions | null;
  open: boolean;
  setOpen: (val: boolean) => void;
  orderItemId?: string;
};

const DetailField: FC<{ label: string; value?: ReactNode }> = ({
  label,
  value,
}) => (
  <div className="grid">
    <span className="text-sm text-gray-500">{label}</span>
    <span className="text-sm text-gray-900 break-words">{value ?? 'N/A'}</span>
  </div>
);

const WalletSection: FC<{ title: string; children: ReactNode }> = ({
  title,
  children,
}) => (
  <div className="rounded-md border border-gray-300 bg-white shadow-sm">
    <div className="border-b border-gray-200 px-4 py-3">
      <span className="text-sm font-semibold text-gray-900">{title}</span>
    </div>
    <div className="px-4 py-4">{children}</div>
  </div>
);

const statusStyles: Record<string, string> = {
  success: 'bg-green-100 text-green-800',
  pending: 'bg-yellow-100 text-yellow-800',
  failed: 'bg-red-100 text-red-800',
};

const TransactionStatusBadge: FC<{ status?: string | null }> = ({ status }) => {
  const normalized = status?.toLowerCase() ?? '';
  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${
        statusStyles[normalized] ?? 'bg-gray-100 text-gray-800'
      }`}
    >
      {status ?? 'N/A'}
    </span>
  );
};

const TransactionDetailDrawer: FC<Props> = ({
  transaction,
  open,
  setOpen,
  orderItemId,
}) => {
  const metadata = parseTransactionMetadata(transaction?.metadata);
  const { orderItem, loading: orderItemLoading } = useOrderItem(
    open ? orderItemId : undefined
  );

  const isCredit = transaction?.direction === 'inbound';
  const description = getMetadataDescription(metadata);

  return (
    <Drawer
      open={open}
      setOpen={setOpen}
      title="Transaction Details"
      description={transaction?.reference ?? undefined}
    >
      {transaction ? (
        <div className="space-y-4">
          <WalletSection title="Overview">
            <div className="grid gap-4 sm:grid-cols-2">
              <DetailField label="Reference" value={transaction.reference} />
              <DetailField
                label="Transaction ID"
                value={
                  <span className="font-mono text-xs">{transaction.id}</span>
                }
              />
              <DetailField
                label="Type"
                value={_.startCase(transaction.type) || 'N/A'}
              />
              <DetailField
                label="Direction"
                value={
                  <span
                    className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${
                      isCredit
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {_.startCase(transaction.direction)}
                  </span>
                }
              />
              <DetailField
                label="Amount"
                value={
                  <span className="font-semibold">
                    {transaction.currency?.code}{' '}
                    {numeral(transaction.amount).format('0,0.00')}
                  </span>
                }
              />
              <DetailField
                label="Status"
                value={<TransactionStatusBadge status={transaction.status} />}
              />
              <DetailField
                label="Created At"
                value={moment(transaction.created_at).format(
                  'Do MMM, YYYY hh:mm A'
                )}
              />
              <DetailField
                label="Updated At"
                value={
                  transaction.updated_at
                    ? moment(transaction.updated_at).format(
                        'Do MMM, YYYY hh:mm A'
                      )
                    : 'N/A'
                }
              />
            </div>
          </WalletSection>

          {description ? (
            <WalletSection title="Description">
              <p className="text-sm text-gray-900 whitespace-pre-wrap">
                {description}
              </p>
            </WalletSection>
          ) : null}

          {orderItemId ? (
            <WalletSection title="Related Order Item">
              {orderItemLoading ? (
                <p className="text-sm text-gray-500">Loading order item...</p>
              ) : orderItem ? (
                <div className="space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <DetailField
                      label="Order Number"
                      value={orderItem.orderNumber}
                    />
                    <DetailField label="Quantity" value={orderItem.quantity} />

                    <DetailField label="SKU" value={orderItem.sku} />
                    <DetailField label="Status" value={orderItem.status} />
                    <DetailField
                      label="Supplier Status"
                      value={orderItem.supplier_status}
                    />

                    <DetailField
                      label="Created At"
                      value={
                        orderItem.createdAt
                          ? moment(orderItem.createdAt).format(
                              'Do MMM, YYYY hh:mm A'
                            )
                          : 'N/A'
                      }
                    />
                  </div>

                  {orderItem.product ? (
                    <Link
                      to={`/products/${orderItem.product.id}/view`}
                      className="block rounded-md border border-gray-200 bg-gray-50 p-4 transition-colors hover:border-primary-300 hover:bg-primary-50/50"
                    >
                      <div className="mb-3 flex items-center justify-between gap-2">
                        <p className="text-sm font-semibold text-gray-900">
                          Product Details
                        </p>
                        <span className="text-xs font-medium text-primary-600">
                          View Product
                        </span>
                      </div>
                      <div className="flex gap-4">
                        {orderItem.product.avatar ? (
                          <img
                            src={orderItem.product.avatar}
                            alt={orderItem.product.title ?? 'Product'}
                            className="h-20 w-20 flex-shrink-0 rounded-md border border-gray-200 bg-white object-contain"
                          />
                        ) : null}
                        <div className="grid flex-1 gap-3 sm:grid-cols-2">
                          <DetailField
                            label="Product Title"
                            value={orderItem.product.title}
                          />
                          <DetailField
                            label="Product SKU"
                            value={orderItem.product.sku}
                          />

                          <DetailField
                            label="Cost Price"
                            value={
                              orderItem.product.cost_price != null
                                ? numeral(orderItem.product.cost_price).format(
                                    '0,0.00'
                                  )
                                : 'N/A'
                            }
                          />
                          <DetailField
                            label="Combination"
                            value={orderItem.product.combination_string}
                          />
                        </div>
                      </div>
                    </Link>
                  ) : null}

                  {orderItem.order ? (
                    <div className="rounded-md border border-gray-200 bg-gray-50 p-4">
                      <div className="mb-3 flex items-center justify-between gap-2">
                        <p className="text-sm font-semibold text-gray-900">
                          Order Details
                        </p>
                        <Link
                          to={`/orders/${orderItem.order.id}`}
                          className="text-xs font-medium text-primary-600 hover:text-primary-700"
                        >
                          View Order
                        </Link>
                      </div>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <DetailField
                          label="Cart Reference"
                          value={orderItem.order.cart_number}
                        />
                        <DetailField
                          label="Payment Status"
                          value={orderItem.order.payment_status}
                        />
                        <DetailField
                          label="Payment Type"
                          value={orderItem.order.payment_type}
                        />

                        <DetailField
                          label="Delivery Amount"
                          value={numeral(
                            orderItem.order.delivery_amount
                          ).format('0,0.00')}
                        />
                        <DetailField
                          label="Tracking Code"
                          value={orderItem.order.tracking_code}
                        />
                        <DetailField
                          label="Customer"
                          value={orderItem.order.customer?.name}
                        />
                        <DetailField
                          label="Reseller"
                          value={orderItem.order.reseller?.name}
                        />
                        <DetailField
                          label="Delivery Location"
                          value={orderItem.order.address?.area}
                        />
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : (
                <p className="text-sm text-gray-500">
                  No order item found for ID {orderItemId}.
                </p>
              )}
            </WalletSection>
          ) : null}
        </div>
      ) : null}
    </Drawer>
  );
};

export default TransactionDetailDrawer;
